import React, {
 useEffect, useRef, useState
} from 'react';
import WebchatScreen, {
 WebchatScreenProps
} from './WebchatScreen';
import './web-chat.css';
import {
  EmailUpdateObject as ApiEmailUpdateObject,
  WebChatConfigType as ApiWebChatConfigType,
} from '../../../types/builderChannel.types';

/**
 * Local stricter type for normalized chat settings.
 */
type WebChatConfigWithSize = Omit<
  ApiWebChatConfigType,
  'primaryColor' | 'secondaryColor' | 'textPrimaryColor' | 'textSecondaryColor'
> & {
  primaryColor: string;
  secondaryColor: string;
  textPrimaryColor: string;
  textSecondaryColor: string;
  chatWidth: number;
  chatHeight: number;
  assistantName: string;
};

interface WebchatWidgetProps {
  chatSettings: ApiWebChatConfigType;
  emailUpdateObject: ApiEmailUpdateObject;
  modalRef: React.RefObject<HTMLDivElement>;
}

const DEFAULTS = {
  primaryColor: '#FFFFFF',
  secondaryColor: '#000000',
  textPrimaryColor: '#000000',
  textSecondaryColor: '#FFFFFF',
  chatWidth: 380,
  chatHeight: 500,
  assistantName: 'BeX AI Assistant',
  includeConvoHistory: false,
  address: '',
};

const WebchatWidget: React.FC<WebchatWidgetProps> = ({
  chatSettings,
  emailUpdateObject,
  modalRef,
}) => {
  const [position, setPosition] = useState<{ right: number }>({
 right: 16
});
  const [modalWidth, setModalWidth] = useState<number>(1100);
  const [isDragging, setIsDragging] = useState(false);
  const [isOverLeft, setIsOverLeft] = useState(false);

  const dragging = useRef<{ startX: number; initialRight: number } | null>(null);
  const draggingOver = useRef<'left' | 'right' | null>(null);
  const resizeTimeout = useRef<number | null>(null);

  const chatWidth = Number(chatSettings?.chatWidth ?? DEFAULTS.chatWidth);

  // Handle modal width init and resize
  useEffect(() => {
    if (modalRef?.current) {
      const rect = modalRef.current.getBoundingClientRect();
      if (rect?.width) setModalWidth(rect.width);
    }
  }, [modalRef]);

  useEffect(() => {
    const handleResize = () => {
      if (resizeTimeout.current) window.clearTimeout(resizeTimeout.current);
      resizeTimeout.current = window.setTimeout(() => {
        const newWidth = modalRef?.current?.getBoundingClientRect()?.width ?? 1100;
        setModalWidth(newWidth);
        endDrag(undefined, newWidth);
      }, 120);
    };

    window.addEventListener('resize', handleResize);
    return () => {
      if (resizeTimeout.current) window.clearTimeout(resizeTimeout.current);
      window.removeEventListener('resize', handleResize);
      document.removeEventListener('mousemove', onDrag);
      document.removeEventListener('mouseup', endDrag);
    };
  }, [modalRef, chatWidth]);

  const startDrag = (e: React.MouseEvent<HTMLDivElement>) => {
    if ((e.target as HTMLElement).closest('[data-no-drag]')) return;
    e.preventDefault();
    setIsDragging(true);
    dragging.current = {
 startX: e.clientX,
initialRight: position.right
};
    document.addEventListener('mousemove', onDrag);
    document.addEventListener('mouseup', endDrag);
  };

  const onDrag = (e: MouseEvent) => {
    if (!dragging.current) return;

    const deltaX = dragging.current.startX - e.clientX;
    const newRight = dragging.current.initialRight + deltaX;
    const maxRight = Math.max(0, (modalWidth || 1100) - Number(chatWidth));

    setPosition({
 right: Math.min(Math.max(0, newRight), maxRight)
});

    if (e.clientX < (modalWidth || 1100) / 2) {
      draggingOver.current = 'left';
      setIsOverLeft(true);
    } else {
      draggingOver.current = 'right';
      setIsOverLeft(false);
    }
  };

  const endDrag = (e?: MouseEvent, newWidth?: number) => {
    setIsDragging(false);
    document.removeEventListener('mousemove', onDrag);
    document.removeEventListener('mouseup', endDrag);

    const usedWidth = newWidth ?? modalWidth ?? 1100;
    const newPosition = usedWidth - Number(chatWidth) - 16;
    const finalSide = draggingOver.current ?? (isOverLeft ? 'left' : 'right');

    setPosition({
 right: finalSide === 'left' ? newPosition : 16
});

    dragging.current = null;
    draggingOver.current = null;
  };

  /** Normalize chat settings */
  const normalizedChatSettings: WebChatConfigWithSize = {
    primaryColor: chatSettings.primaryColor ?? DEFAULTS.primaryColor,
    secondaryColor: chatSettings.secondaryColor ?? DEFAULTS.secondaryColor,
    textPrimaryColor: chatSettings.textPrimaryColor ?? DEFAULTS.textPrimaryColor,
    textSecondaryColor: chatSettings.textSecondaryColor ?? DEFAULTS.textSecondaryColor,
    chatWidth: Number(chatSettings.chatWidth ?? DEFAULTS.chatWidth),
    chatHeight: Number(chatSettings.chatHeight ?? DEFAULTS.chatHeight),
    assistantName: chatSettings.assistantName ?? DEFAULTS.assistantName,
    ...chatSettings,
  } as WebChatConfigWithSize;

  /** Normalize email update object */
  const normalizedEmailUpdateObject: WebchatScreenProps['emailUpdateObject'] = {
    ...(emailUpdateObject as any),
    address:
      (emailUpdateObject as any)?.address ??
      (emailUpdateObject as any)?.email ??
      DEFAULTS.address,
    includeConvoHistory:
      (emailUpdateObject as any)?.includeConvoHistory ?? DEFAULTS.includeConvoHistory,
    webChatConfig:
      (emailUpdateObject as any)?.webChatConfig ??
      (emailUpdateObject as any)?.chatSettings ??
      undefined,
  };

  return (
      <WebchatScreen
      chatSettings={normalizedChatSettings}
      emailUpdateObject={normalizedEmailUpdateObject}
      dragObj={{
        startDrag,
        isDragging,
        position,
        isOverLeft,
      }}
    />
  );
};

export default WebchatWidget;
